/**
 * 权限管理 API
 * 对应 foundation-service (7090)
 *
 * 后端使用 snake_case + string 类型字段，前端使用 camelCase + 枚举
 * 本文件负责双向转换
 */

import { unifiedApi } from '@/utils/unifiedApi';
import handleApiResponse from '@/utils/apiResponseHandler';
import type { Permission, PermissionQueryParams, PermissionFormData } from './types';
import { PermissionType, PermissionStatus } from './types';

// ===== 后端 ↔ 前端 类型映射 =====

/** 后端类型字符串 → 前端枚举 */
const mapTypeFromApi = (type: string): PermissionType => {
  switch (type) {
    case 'directory': return PermissionType.DIRECTORY;
    case 'menu': return PermissionType.MENU;
    case 'button': return PermissionType.BUTTON;
    case 'api': return PermissionType.BUTTON;
    default: return PermissionType.DIRECTORY;
  }
};

/** 前端枚举 → 后端类型字符串 */
const mapTypeToApi = (type: PermissionType): string => {
  switch (type) {
    case PermissionType.DIRECTORY: return 'directory';
    case PermissionType.MENU: return 'menu';
    case PermissionType.BUTTON: return 'button';
    default: return 'menu';
  }
};

/** 后端权限节点 → 前端 Permission 类型 */
const mapPermissionFromApi = (raw: any): Permission => ({
  id: String(raw.id),
  code: raw.code || '',
  title: raw.name || '',
  name: raw.name || '',
  menuType: mapTypeFromApi(raw.type),
  parentId: raw.parent_id ? String(raw.parent_id) : undefined,
  level: 0,
  sort: raw.sort || 0,
  status: (raw.status ?? 1) as PermissionStatus,
  description: raw.description || '',
  path: raw.path || '',
  method: raw.method || '',
  createdAt: raw.created_at || '',
  updatedAt: raw.updated_at || '',
  children: raw.children?.map(mapPermissionFromApi),
} as Permission);

/** 前端 PermissionFormData → 后端请求格式 */
const mapFormToApi = (data: PermissionFormData) => ({
  parent_id: data.parentId ? Number(data.parentId) : 0,
  name: data.name,
  code: data.code,
  type: mapTypeToApi(data.type),
  path: data.path || '',
  method: (data as any).method || '',
  description: data.description || '',
  status: data.status ?? PermissionStatus.NORMAL,
  sort: data.sort ?? 0,
});

// ===== API 函数 =====

/** 获取权限树 */
export const getPermissionList = async (params: PermissionQueryParams = {}) => {
  const res: any = await handleApiResponse(
    unifiedApi.service('permission').get('/permissions/tree', { params })
  );
  // 后端返回 { tree: [...] }，需要提取并映射
  const rawTree = res?.data?.tree || res?.data || [];
  const tree = Array.isArray(rawTree) ? rawTree.map(mapPermissionFromApi) : [];
  return { ...res, data: tree };
};

/** 获取权限详情 */
export const getPermissionById = async (id: string) => {
  const res: any = await handleApiResponse(
    unifiedApi.service('permission').get(`/permissions/${id}`)
  );
  const raw = res?.data?.data ?? res?.data;
  return { ...res, data: raw ? mapPermissionFromApi(raw) : null };
};

/** 创建权限 */
export const createPermission = async (data: PermissionFormData) => {
  const apiData = mapFormToApi(data);
  const res: any = await handleApiResponse(
    unifiedApi.service('permission').post('/permissions', apiData)
  );
  const raw = res?.data?.data ?? res?.data;
  return { ...res, data: raw ? mapPermissionFromApi(raw) : null };
};

/** 更新权限 */
export const updatePermission = async (id: string, data: Partial<PermissionFormData>) => {
  const apiData: Record<string, any> = {};
  if (data.parentId !== undefined) apiData.parent_id = data.parentId ? Number(data.parentId) : 0;
  if (data.name !== undefined) apiData.name = data.name;
  if (data.code !== undefined) apiData.code = data.code;
  if (data.type !== undefined) apiData.type = mapTypeToApi(data.type);
  if (data.path !== undefined) apiData.path = data.path;
  if (data.description !== undefined) apiData.description = data.description;
  if (data.status !== undefined) apiData.status = data.status;
  if (data.sort !== undefined) apiData.sort = data.sort;

  return handleApiResponse(
    unifiedApi.service('permission').put<any>(`/permissions/${id}`, apiData)
  );
};

/** 删除权限 */
export const deletePermission = async (id: string) => {
  return handleApiResponse(
    unifiedApi.service('permission').delete<any>(`/permissions/${id}`)
  );
};

/** 获取权限关联的角色列表 */
export const getPermissionRoles = async (permissionId: string) => {
  return handleApiResponse(
    unifiedApi.service('permission').get<any>(`/permissions/${permissionId}/roles`)
  );
};

/** 权限 API 对象 */
export const permissionApi = {
  getPermissionList,
  getPermissionById,
  createPermission,
  updatePermission,
  deletePermission,
  getPermissionRoles,
};

export default permissionApi;
